import Global from './global';
export class BottomSheet {
    private sheet: HTMLElement;
    private handle: HTMLElement;
    private body: HTMLElement;

    private closeButton: HTMLElement | null = null;

    private startY = 0;
    private isMouseTouching = false;
    private isDragging = false;
    private startTranslateY = 0;
    private nextTranslate = 90;
    private animationFrameId: number | null = null;

    private isBodyMouseDown = false;
    private isDraggingBody = false;
    private dragThreshold = 5; // px before we consider it a drag
    private bodyStartY = 0;
    private bodyStartScrollTop = 0;

    // For inertia
    private lastY = 0;
    private lastTime = 0;
    private velocityY = 0;
    private momentumFrame: number | null = null;

    private closedY = 92;

    private readonly SNAP_POINTS = [0, this.closedY];

    private touchStartY = 0;
    private isTouching = false;

    constructor(sheetId: string, handleId: string, bodyId: string) {
        const sheetEl = document.getElementById(sheetId);
        const handleEl = document.getElementById(handleId);
        const bodyEl = document.getElementById(bodyId);

        if (!sheetEl || !handleEl || !bodyEl) {
            throw new Error('BottomSheet: One or more elements not found by provided IDs.');
        }

        this.sheet = sheetEl;
        this.handle = handleEl;
        this.body = bodyEl;
    }

    public init(): void {
        this.closeButton = document.getElementById('sheetCloseButton');
        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.animateTo(this.closedY));
        }

        this.setTranslateY(this.closedY);
        this.updateOpenClass(this.closedY);

        this.handle.addEventListener('mousedown', this.onDragStart);
        this.handle.addEventListener('touchstart', this.onDragStart);

        this.body.addEventListener('touchstart', this.onBodyTouchStart, { passive: true });
        this.body.addEventListener('touchmove', this.onBodyTouchMove, { passive: false });
        this.body.addEventListener('touchend', this.onBodyTouchEnd);

        this.body.addEventListener('wheel', this.onBodyMouseWheel, { passive: false });

        this.body.addEventListener('mousedown', this.onBodyMouseDown);

        // Lazy loading images
        this.body.addEventListener('scroll', Global.checkLazyImages);
        this.handle.addEventListener('touchmove', Global.checkLazyImages);
        this.handle.addEventListener('mousemove', Global.checkLazyImages);
    }

    public destroy(): void {
        this.handle.removeEventListener('mousedown', this.onDragStart);
        this.handle.removeEventListener('touchstart', this.onDragStart);

        this.body.removeEventListener('touchstart', this.onBodyTouchStart);
        this.body.removeEventListener('touchmove', this.onBodyTouchMove);
        this.body.removeEventListener('touchend', this.onBodyTouchEnd);

        this.body.removeEventListener('wheel', this.onBodyMouseWheel);

        this.removeDocumentListeners();

        this.body.removeEventListener('mousedown', this.onBodyMouseDown);
        this.body.removeEventListener('scroll', Global.checkLazyImages);
        document.removeEventListener('mousemove', this.onBodyMouseMove);
        document.removeEventListener('mouseup', this.onBodyMouseUp);
    }

    private getSheetHeight(): number {
        return this.sheet.getBoundingClientRect().height;
    }

    private getTranslateY(): number {
        const match = this.sheet.style.transform.match(/translateY\((.*)%\)/);
        return match ? parseFloat(match[1]) : 90;
    }

    private setTranslateY = (percentage: number): void => {
        this.nextTranslate = percentage;
        if (!this.animationFrameId) {
            this.animationFrameId = requestAnimationFrame(() => {
                this.sheet.style.transform = `translateY(${this.nextTranslate}%)`;
                this.animationFrameId = null;
                this.updateOpenClass(this.nextTranslate);
            });
        }
    };

    private closestSnapPoint(value: number): number {
        return this.SNAP_POINTS.reduce((prev, curr) =>
            Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
        );
    }

    private updateOpenClass(translateY: number): void {
        const fullyOpen = translateY === 0;

        if (fullyOpen) {
            this.sheet.classList.add('open');
            Global.checkLazyImages();
            if (this.closeButton) this.closeButton.classList.add('show');
        } else {
            this.sheet.classList.remove('open');
            if (this.closeButton) this.closeButton.classList.remove('show');
        }
    }

    private onDragStart = (e: MouseEvent | TouchEvent): void => {
        this.isDragging = true;
        this.startY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
        this.startTranslateY = this.getTranslateY();

        this.sheet.style.transition = 'none';
        this.handle.style.pointerEvents = 'none';

        document.addEventListener('mousemove', this.onDrag);
        document.addEventListener('touchmove', this.onDrag, { passive: false });
        document.addEventListener('mouseup', this.onDragEnd);
        document.addEventListener('touchend', this.onDragEnd);
    };

    private onDrag = (e: MouseEvent | TouchEvent): void => {
        if (!this.isDragging) return;

        const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
        const deltaY = clientY - this.startY;

        const sheetHeight = this.getSheetHeight();
        const deltaPercent = (deltaY / sheetHeight) * 100;
        let next = Math.min(Math.max(this.startTranslateY + deltaPercent, 0), 90);

        e.preventDefault();

        this.setTranslateY(next);

        Global.checkLazyImages();
    };

    private onDragEnd = (): void => {
        if (!this.isDragging) return;
        this.isDragging = false;

        const current = this.getTranslateY();
        const dragDelta = current - this.startTranslateY;

        if (Math.abs(dragDelta) < 5 && this.startTranslateY === this.closedY) {
            this.animateTo(0, 0.3);

            const onTransitionEnd = () => {
                Global.checkLazyImages();
                this.sheet.removeEventListener('transitionend', onTransitionEnd);
            };

            this.sheet.addEventListener('transitionend', onTransitionEnd);
        } else {
            // Normal snapping logic
            let snap: number;
            const currentIndex = this.SNAP_POINTS.indexOf(this.closestSnapPoint(this.startTranslateY));
            const goingUp = dragDelta < 0;

            if (Math.abs(dragDelta) > 10) {
                if (goingUp && currentIndex > 0) {
                    snap = this.SNAP_POINTS[currentIndex - 1];
                } else if (!goingUp && currentIndex < this.SNAP_POINTS.length - 1) {
                    snap = this.SNAP_POINTS[currentIndex + 1];
                } else {
                    snap = this.SNAP_POINTS[currentIndex];
                }
            } else {
                snap = this.SNAP_POINTS[currentIndex];
            }

            this.animateTo(snap, 0.3);
        }

        this.handle.style.pointerEvents = '';
        this.removeDocumentListeners();
    };

    private onBodyTouchStart = (e: TouchEvent): void => {
        this.isTouching = true;
        this.touchStartY = e.touches[0].clientY;
    };

    private onBodyTouchMove = (e: TouchEvent): void => {
        if (!this.isTouching || e.touches.length !== 1) return;

        const currentY = e.touches[0].clientY;
        const deltaY = currentY - this.touchStartY;

        const isAtTop = this.body.scrollTop <= 0;
        const isPullingDown = deltaY > 0;

        if (this.getTranslateY() === 0 && isAtTop && isPullingDown) {
            if (deltaY > 50) {
                e.preventDefault();
                this.animateTo(this.closedY, 0.3);
                this.isTouching = false;
                return;
            }

            e.preventDefault();
            this.onDragStart(e);
            this.isTouching = false;
        }
    };

    private onBodyTouchEnd = (): void => {
        this.isTouching = false;
    };

    private onBodyMouseWheel = (e: WheelEvent) => {
        const sheetFullyOpen = this.getTranslateY() === 0;
        const isAtTop = this.body.scrollTop <= 0;

        if (sheetFullyOpen && isAtTop && e.deltaY < 0) {
            e.preventDefault();
            this.animateTo(this.closedY, 0.3);
        }
    };

    private onBodyMouseDown = (e: MouseEvent): void => {
        if (e.button !== 0) return;

        this.isBodyMouseDown = true;
        this.isDraggingBody = false;
        this.bodyStartY = e.clientY;
        this.bodyStartScrollTop = this.body.scrollTop;

        this.lastY = e.clientY;
        this.lastTime = performance.now();
        this.velocityY = 0;

        if (this.momentumFrame) {
            cancelAnimationFrame(this.momentumFrame);
            this.momentumFrame = null;
        }

        e.preventDefault();

        document.addEventListener('mousemove', this.onBodyMouseMove);
        document.addEventListener('mouseup', this.onBodyMouseUp);
    };

    private onBodyMouseMove = (e: MouseEvent): void => {
        if (!this.isBodyMouseDown) return;

        const deltaTotal = Math.abs(e.clientY - this.bodyStartY);
        if (deltaTotal > this.dragThreshold) {
            this.isDraggingBody = true;
        }

        if (this.isDraggingBody) {
            const now = performance.now();
            const deltaY = e.clientY - this.bodyStartY;
            this.body.scrollTop = this.bodyStartScrollTop - deltaY;

            // track velocity
            const dt = now - this.lastTime;
            if (dt > 0) {
                this.velocityY = (this.lastY - e.clientY) / dt; // px per ms
            }

            this.lastY = e.clientY;
            this.lastTime = now;
        }
    };

    private onBodyMouseUp = (e: MouseEvent): void => {
        this.isBodyMouseDown = false;
        document.removeEventListener('mousemove', this.onBodyMouseMove);
        document.removeEventListener('mouseup', this.onBodyMouseUp);

        if (this.isDraggingBody) {
            // prevent accidental click on links/buttons
            const preventClick = (clickEvent: MouseEvent) => {
                clickEvent.stopPropagation();
                clickEvent.preventDefault();
                this.body.removeEventListener('click', preventClick, true);
            };
            this.body.addEventListener('click', preventClick, true);

            this.applyMomentum();
        }
    };

    private applyMomentum = (): void => {
        const decay = 0.95; // friction (closer to 1 = slower decay)
        const minVelocity = 0.02; // stop threshold (px/ms)

        const step = () => {
            if (Math.abs(this.velocityY) < minVelocity) {
                this.momentumFrame = null;
                return;
            }

            const sheetFullyOpen = this.getTranslateY() === 0;
            const atTop = this.body.scrollTop <= 0;

            // 👇 If sheet fully open, at top, and inertia still pushing upward → close
            if (sheetFullyOpen && atTop && this.velocityY < 0) {
                this.momentumFrame = null;
                this.animateTo(this.closedY, 0.3);
                return;
            }

            this.body.scrollTop += this.velocityY * 16; // 16ms per frame ~ 60fps
            this.velocityY *= decay;

            this.momentumFrame = requestAnimationFrame(step);
        };

        this.momentumFrame = requestAnimationFrame(step);
    };

    private removeDocumentListeners(): void {
        document.removeEventListener('mousemove', this.onDrag);
        document.removeEventListener('touchmove', this.onDrag);
        document.removeEventListener('mouseup', this.onDragEnd);
        document.removeEventListener('touchend', this.onDragEnd);
    }

    private animateTo(percent: number, duration = 0.3) {
        this.sheet.style.transition = `transform ${duration}s ease`;
        // Force a layout so the transition takes effect
        this.sheet.getBoundingClientRect();
        this.setTranslateY(percent);
    }
}
